'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';

type Theme = 'dark' | 'light' | 'ogre' | 'purple' | 'crazy';

function getWordmarkGradient(theme: Theme, crazyColors?: { c1: string; c2: string }) {
  if (theme === 'crazy' && crazyColors) return `linear-gradient(135deg, ${crazyColors.c1} 0%, ${crazyColors.c2} 100%)`;
  if (theme === 'ogre') return 'linear-gradient(135deg, #ffffff 0%, #86efac 50%, #22c55e 100%)';
  if (theme === 'light') return 'linear-gradient(90deg, #ffffff 0%, #7B5EA7 100%)';
  if (theme === 'purple') return 'linear-gradient(90deg, #ffffff 0%, #45d5e6 100%)';
  return 'linear-gradient(135deg, #ffffff 0%, #93c5fd 50%, #3b82f6 100%)';
}

function getLogoFilter(theme: Theme, crazyHue?: number) {
  if (theme === 'crazy' && crazyHue !== undefined) {
    return `brightness(0) saturate(100%) invert(60%) sepia(80%) saturate(500%) hue-rotate(${crazyHue}deg) brightness(1.1)`;
  }
  if (theme === 'ogre') return 'brightness(0) saturate(100%) invert(72%) sepia(60%) saturate(400%) hue-rotate(90deg) brightness(1.1)';
  if (theme === 'light') return 'brightness(0) saturate(100%) invert(40%) sepia(60%) saturate(400%) hue-rotate(240deg) brightness(0.9)';
  if (theme === 'purple') return 'brightness(0) saturate(100%) invert(75%) sepia(60%) saturate(400%) hue-rotate(160deg) brightness(1.1)';
  return 'none';
}

function getHeaderStyle(theme: Theme): React.CSSProperties {
  return { backgroundColor: 'rgba(5,15,40,0.97)', borderBottomColor: '#0f2060' };
}

function getCrazyColors() {
  const root = document.documentElement;
  return {
    c1: root.style.getPropertyValue('--crazy-wordmark-color1') || '#ff00ff',
    c2: root.style.getPropertyValue('--crazy-wordmark-color2') || '#00ffff',
    hue: parseInt(root.style.getPropertyValue('--crazy-logo-hue') || '0', 10),
  };
}

export default function Topbar() {
  const [theme, setTheme] = useState<Theme>('dark');
  const [crazyColors, setCrazyColors] = useState<{ c1: string; c2: string; hue: number } | null>(null);

  useEffect(() => {
    const saved = (localStorage.getItem('giga-theme') as Theme) || 'dark';
    setTheme(saved);
    if (saved === 'crazy') {
      // Small delay to let ThemeSwitcher set the CSS vars first
      setTimeout(() => setCrazyColors(getCrazyColors()), 50);
    }

    const observer = new MutationObserver(() => {
      const current = (document.documentElement.getAttribute('data-theme') as Theme) || 'dark';
      setTheme(current);
      if (current === 'crazy') {
        setTimeout(() => setCrazyColors(getCrazyColors()), 50);
      } else {
        setCrazyColors(null);
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    // Also watch for CSS variable changes (crazy color cycling)
    const styleObserver = new MutationObserver(() => {
      const current = (document.documentElement.getAttribute('data-theme') as Theme) || 'dark';
      if (current === 'crazy') {
        setCrazyColors(getCrazyColors());
      }
    });
    styleObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    return () => {
      observer.disconnect();
      styleObserver.disconnect();
    };
  }, []);

  const wordmarkGradient = getWordmarkGradient(theme, crazyColors ? { c1: crazyColors.c1, c2: crazyColors.c2 } : undefined);
  const logoFilter = getLogoFilter(theme, crazyColors?.hue);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-md"
      style={getHeaderStyle(theme)}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700;900&display=swap');
        .giga-wordmark {
          font-family: 'Orbitron', sans-serif;
          font-weight: 900;
          letter-spacing: 0.04em;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 h-16 flex items-center justify-between gap-2">
        <Link href="/business-listings" className="flex items-center gap-2 sm:gap-3 group min-w-0">
          <Image
            src="/assets/giga-logo-icon.svg"
            alt="GigaMarketplace logo"
            width={36}
            height={27}
            className="flex-shrink-0"
            style={{ filter: logoFilter }}
          />
          <div className="flex flex-col min-w-0">
            <span
              className="giga-wordmark text-base sm:text-xl truncate"
              style={{ backgroundImage: wordmarkGradient }}
            >
              GigaMarketplace
            </span>
            <span className="text-zinc-500 text-[10px] leading-none mt-0.5" style={{ WebkitTextFillColor: 'rgb(113 113 122)' }}>Made by Silas</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-shrink-0">
          <Link
            href="/business-listings"
            className="px-4 py-2 text-sm font-medium text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-lg transition-all duration-150"
          >
            Browse
          </Link>
          <Link
            href="/add-business"
            className="ml-2 px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-500 active:scale-95 text-white rounded-lg transition-all duration-150"
          >
            Add Your Business
          </Link>
        </nav>

        {/* Mobile CTA */}
        <Link
          href="/add-business"
          className="md:hidden flex-shrink-0 px-2.5 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 active:scale-95 text-white rounded-lg transition-all duration-150 whitespace-nowrap"
        >
          Add Business
        </Link>
      </div>
    </header>
  );
}